// src/components/Login/LoginModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useFileContext } from '../../ContextAPI/FileContext';
import { signIn, confirmSignIn, resetPassword, confirmResetPassword, signOut, updateMFAPreference } from 'aws-amplify/auth';
import axios from 'axios';
import QRCode from 'qrcode';
import rdv from '../../assets/rdv-logoW.png';
// NewPasswordModal is no longer needed - reset password is handled as a step
import MfaGuideModal from './MfaGuideModal';
import FingerprintIcon from '../Account/FingerprintIcon';
import { isMobileDevice } from '../../utils/device';
import { baseApi, API_BASE_URL } from '../../API/api';
import './LoginModal.css';

const API = API_BASE_URL;

// Offer passkeys only on mobile, and only where the platform authenticator API
// exists. Desktop/web stays password + TOTP.
const PASSKEY_SUPPORTED =
  typeof window !== 'undefined' && !!window.PublicKeyCredential && isMobileDevice();


const LoginModal = ({ open, handleClose, asPage = false, onSuccess, onClose }) => {
  const currentYear = new Date().getFullYear();
  // When asPage, we're always "open" (full page); otherwise respect open prop
  const isVisible = asPage || open;
  const doClose = () => {
    if (asPage && onClose) onClose();
    else if (handleClose) handleClose();
  };
  const doSuccess = () => {
    // Remember the email so returning users don't retype it (fewer taps before the passkey).
    try { if (email && email.trim()) localStorage.setItem('rdvLastLoginEmail', email.trim()); } catch (e) { /* ignore */ }
    if (asPage && onSuccess) onSuccess();
    else if (handleClose) handleClose();
  };
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem('rdvLastLoginEmail') || ''; } catch (e) { return ''; }
  });
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [tempUser, setTempUser] = useState(null);
  const { setAuth } = useFileContext();

  // MFA states
  const [mfaStep, setMfaStep] = useState(null); // 'setup', 'verify', 'password', or null
  const [mfaCode, setMfaCode] = useState('');
  const [mfaQrCode, setMfaQrCode] = useState('');
  const [mfaSecretKey, setMfaSecretKey] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [mfaUser, setMfaUser] = useState(null);
  const [mfaVerifying, setMfaVerifying] = useState(false);


  // Reset Password states
  // NOTE: There are TWO different flows that both use the password step:
  // 1. NEW USER FLOW: Login with temp password → Set new password (passwordFlowType: 'new-user')
  //    - Back button should return to login form
  // 2. RESET PASSWORD FLOW: Forgot password → Email → Code → Set new password (passwordFlowType: 'reset-password')
  //    - Back button should return to code step
  const [resetPasswordStep, setResetPasswordStep] = useState(null); // 'email', 'code', 'password', or null
  const [passwordFlowType, setPasswordFlowType] = useState(null); // 'new-user' or 'reset-password' - tracks which flow we're in
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // MFA Guide Modal state
  const [showMfaGuide, setShowMfaGuide] = useState(false);
  
  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  // Passkey (WebAuthn) sign-in state
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeyError, setPasskeyError] = useState('');
  const autoPasskeyTriedRef = useRef(false);

  // Navigation history stack to track where user came from
  const [navigationStack, setNavigationStack] = useState([]);
  const navigationStackRef = useRef([]);

  // Navigation management functions
  const pushToNavigationStack = (page) => {
    setNavigationStack(prev => {
      const newStack = [...prev, page];
      navigationStackRef.current = newStack;
      return newStack;
    });
  };

  const popFromNavigationStack = () => {
    let previousPage = 'login';
    
    setNavigationStack(prev => {
      if (prev.length === 0) {
        previousPage = 'login';
        navigationStackRef.current = [];
        return prev;
      }
      // Get the page we're going back to (second to last, or 'login' if only one item)
      previousPage = prev.length > 1 ? prev[prev.length - 2] : 'login';
      const newStack = [...prev];
      newStack.pop();
      navigationStackRef.current = newStack;
      return newStack;
    });
    
    return previousPage;
  };

  const clearNavigationStack = () => {
    setNavigationStack([]);
    navigationStackRef.current = [];
  };

  const getPreviousPage = () => {
    const stack = navigationStackRef.current.length > 0 ? navigationStackRef.current : navigationStack;
    if (stack.length === 0) return 'login';
    return stack[stack.length - 1];
  };

  // Function to check user MFA status in Cognito
  const checkUserMfaStatus = async (email) => {
    try {
      console.log('🔍 Checking MFA status for user:', email);
      
      const response = await axios.post(`${API}/api/admin/check-user-mfa-status`, {
        email: email
      });
      
      console.log('📊 MFA Status Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error checking MFA status:', error);
      return null;
    }
  };

  // Function to get username by email
  const getUsernameByEmail = async (email) => {
    try {
      console.log('Getting username for email:', email);
      console.log('Making request to:', `${API}/api/admin/get-username-by-email`);
      
      const response = await axios.post(`${API}/api/admin/get-username-by-email`, {
        email: email
      });
      
      console.log('Username lookup response:', response.data);
      return response.data.username;
    } catch (error) {
      console.error('Could not get username by email:', error);
      console.error('Error details:', error.response?.data);
      return null;
    }
  };

  // Function to verify user email in Cognito using AWS CLI admin command
  const verifyUserEmailInCognito = async (username, userEmail) => {
    try {
      console.log(`Verifying email for user: ${username}, email: ${userEmail}`);
      
      const response = await axios.post(`${API}/api/admin/verify-email-cli`, {
        username: username,  // Use the actual username (UUID)
        email: userEmail     // Use the actual email
      });
      
      console.log('Email verified successfully using AWS CLI:', response.data);
      return response.data;
      
    } catch (error) {
      console.warn('Could not verify email via AWS CLI:', error);
      
      // Handle axios errors (HTTP errors)
      if (error.response) {
        // Server responded with error status
        throw new Error(`Email verification failed: ${error.response.data?.error || 'Server error'}`);
      }
      
      // Handle network or other errors
      throw error;
    }
  };

  // MFA Setup function
  const handleMfaSetup = async (totpSetupDetails) => {
    try {
      console.log('Setting up MFA (v6 TOTP setup challenge)');

      // v6: the shared secret comes from the sign-in challenge's totpSetupDetails,
      // not from a separate Auth.setupTOTP(user) call.
      const secretKey = totpSetupDetails.sharedSecret;

      // Debug logging
      console.log('Secret key from Cognito:', secretKey);

      // Create TOTP URI in the standard format for authenticator apps
      // Format: otpauth://totp/AccountName?secret=SECRET&issuer=IssuerName
      const accountName = 'MFA';
      const issuerName = 'RDV Systems';
      
      // Ensure the secret key is properly formatted (remove any spaces, convert to uppercase)
      const cleanSecretKey = secretKey.replace(/\s/g, '').toUpperCase();
      
      // Create the TOTP URI with proper encoding
      const totpUri = `otpauth://totp/${encodeURIComponent(issuerName)}:${encodeURIComponent(accountName)}?secret=${cleanSecretKey}&issuer=${encodeURIComponent(issuerName)}&algorithm=SHA1&digits=6&period=30`;
      
      console.log('Generated TOTP URI:', totpUri);
      
      // Generate QR code as data URL
      const qrCodeDataUrl = await QRCode.toDataURL(totpUri, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M'
      });
      
      setMfaSecretKey(secretKey);
      setMfaQrCode(qrCodeDataUrl);
      setMfaUser(true);
      
      // Note: Navigation stack should be pushed BEFORE calling this function
      // This function just sets up the MFA state
      setMfaStep('setup');
      setMfaError('');
      
      console.log('MFA setup completed successfully');
      console.log('MFA step set to:', 'setup');
      console.log('MFA secret key set to:', secretKey);
      
    } catch (error) {
      console.error('MFA setup error:', error);
      console.error('MFA setup error details:', error.message);
      setMfaError('Failed to setup MFA. Please try again.');
    }
  };

  // MFA Verification function
  const handleMfaVerification = async (user, code) => {
    if (mfaVerifying) return;
    if (!code || code.length !== 6) {
      setMfaError('Please enter a 6-digit verification code.');
      return;
    }

    setMfaError(''); // Clear stale error immediately to prevent false shake
    setMfaVerifying(true);
    try {
      console.log('🔐 MFA Verification Debug:');
      console.log('  - Code:', code);
      console.log('  - User Object:', user);
      console.log('  - User Attributes:', user?.attributes);
      console.log('  - User Challenge Name:', user?.challengeName);
      console.log('  - User MFA Preferences:', user?.preferredMFA);
      
      // v6: complete the TOTP-setup sign-in challenge.
      const { isSignedIn } = await confirmSignIn({ challengeResponse: code });
      console.log('✅ MFA setup verification successful, isSignedIn:', isSignedIn);
      
      // Explicitly activate TOTP as preferred (don't rely on auto-activation).
      if (isSignedIn) {
        try { await updateMFAPreference({ totp: 'PREFERRED' }); }
        catch (prefErr) { console.warn('Could not set TOTP preference:', prefErr); }
        try { sessionStorage.removeItem('mfaSetupRequired'); } catch (e) { /* ignore */ }
      }
      
      // Success - MFA verification complete, user is authenticated
      console.log('MFA verification successful, user is authenticated');
      setAuth(true, email);
      doSuccess();
      resetMfaState();
      setTempUser(null);
      
    } catch (error) {
      console.error('❌ MFA verification error:', error);
      console.error('  - Error Type:', error.name);
      console.error('  - Error Message:', error.message);
      console.error('  - Error Code:', error.code);
      
      // More specific error messages based on error type
      if ((error.name || error.code) === 'CodeMismatchException') {
        setMfaError('Invalid verification code. Please try again.');
        setMfaCode('');
        setTimeout(() => document.querySelector('.mfa-code-input-container .mfa-code-input')?.focus(), 0);
      } else if ((error.name || error.code) === 'ExpiredCodeException') {
        setMfaError('Code has expired. Please generate a new code and try again.');
        setMfaCode('');
        setTimeout(() => document.querySelector('.mfa-code-input-container .mfa-code-input')?.focus(), 0);
      } else if ((error.name || error.code) === 'NotAuthorizedException') {
        setMfaError('Authentication failed. Please try again.');
        setMfaCode('');
        setTimeout(() => document.querySelector('.mfa-code-input-container .mfa-code-input')?.focus(), 0);
      } else {
        setMfaError('Invalid verification code. Please try again.');
        setMfaCode('');
        setTimeout(() => document.querySelector('.mfa-code-input-container .mfa-code-input')?.focus(), 0);
      }
    } finally {
      setMfaVerifying(false);
    }
  };

  // MFA Code Confirmation (for existing MFA users)
  const handleMfaCodeConfirm = async (codeOverride) => {
    const codeToUse = codeOverride ?? mfaCode;
    if (!codeToUse || codeToUse.length !== 6) {
      setMfaError('Please enter a 6-digit verification code.');
      return;
    }
    if (mfaVerifying) return;

    setMfaError(''); // Clear stale error immediately to prevent false shake
    setMfaVerifying(true);
    try {
      console.log('🔐 MFA Code Confirmation Debug:');
      console.log('  - Code:', codeToUse);
      console.log('  - MFA User Object:', mfaUser);
      console.log('  - MFA User Attributes:', mfaUser?.attributes);
      console.log('  - MFA User Challenge Name:', mfaUser?.challengeName);
      console.log('  - MFA User MFA Preferences:', mfaUser?.preferredMFA);
      
      // Confirm sign in with MFA code (v6 keeps the challenge state internally)
      const { isSignedIn } = await confirmSignIn({ challengeResponse: codeToUse });
      console.log('✅ MFA confirmation successful, isSignedIn:', isSignedIn);

      // Passed a TOTP challenge => account has TOTP; clear any stale setup flag.
      try { sessionStorage.removeItem('mfaSetupRequired'); } catch (e) { /* ignore */ }
      // Success - user is authenticated
      setAuth(true, email);
      doSuccess();
      resetMfaState();
      
    } catch (error) {
      console.error('❌ MFA confirmation error:', error);
      console.error('  - Error Type:', error.name);
      console.error('  - Error Message:', error.message);
      console.error('  - Error Code:', error.code);
      
      // More specific error messages based on error type
      if ((error.name || error.code) === 'CodeMismatchException') {
        setMfaError('Invalid verification code. Please try again.');
        setMfaCode('');
        setTimeout(() => document.querySelector('.mfa-code-input-container .mfa-code-input')?.focus(), 0);
      } else if ((error.name || error.code) === 'ExpiredCodeException') {
        setMfaError('Code has expired. Please generate a new code and try again.');
        setMfaCode('');
        setTimeout(() => document.querySelector('.mfa-code-input-container .mfa-code-input')?.focus(), 0);
      } else if ((error.name || error.code) === 'NotAuthorizedException') {
        setMfaError('Authentication failed. Please try again.');
        setMfaCode('');
        setTimeout(() => document.querySelector('.mfa-code-input-container .mfa-code-input')?.focus(), 0);
      } else {
        setMfaError('Invalid verification code. Please try again.');
        setMfaCode('');
        setTimeout(() => document.querySelector('.mfa-code-input-container .mfa-code-input')?.focus(), 0);
      }
    } finally {
      setMfaVerifying(false);
    }
  };

  // Reset MFA state
  const resetMfaState = () => {
    setMfaStep(null);
    setMfaCode('');
    setMfaQrCode('');
    setMfaSecretKey('');
    setMfaError('');
    setMfaVerifying(false);
    setMfaUser(null);
    setTempUser(null); // Also clear temp user when resetting MFA
  };

  // Reset Password Handler Functions
  const handleSendResetCode = async () => {
    if (!resetEmail.trim()) {
      setResetError('Please enter your email address');
      return;
    }

    setResetLoading(true);
    setResetError('');

    try {
      await resetPassword({ username: resetEmail.trim() });
      pushToNavigationStack('reset-password-email'); // Track that we came from email step
      setResetPasswordStep('code');
    } catch (error) {
      console.error('Forgot password error:', error);
      setResetError('Failed to send verification code. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  const handlePasswordSubmit = async () => {
    if (!isPasswordValid() || newPassword !== confirmPassword) {
      setResetError('Please check your password requirements');
      return;
    }

    setResetLoading(true);
    setResetError('');

    try {
      // Check if this is a temporary password user (from login flow)
      if (tempUser && !resetCode) {
        // v6: the new-password challenge is part of the same sign-in session
        // (no standalone user object to pass — do NOT call signIn again).
        const { isSignedIn, nextStep } = await confirmSignIn({ challengeResponse: newPassword });
        console.log('Password changed for temp-password user. isSignedIn:', isSignedIn, 'nextStep:', nextStep?.signInStep);

        if (nextStep?.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
          // New user must now set up TOTP MFA
          try {
            const flowSource = passwordFlowType === 'new-user' ? 'new-user-password' : 'reset-password';
            pushToNavigationStack(flowSource); // Track that we came from password step
            await handleMfaSetup(nextStep.totpSetupDetails);
            setResetPasswordStep(null);
            setPasswordFlowType(null); // Clear flow type when moving to MFA
            // Don't clear tempUser yet - we need it for MFA setup
          } catch (error) {
            console.error('Failed to setup MFA after password change:', error);
            popFromNavigationStack();
            setTempUser(null);
            doClose();
          }
        } else if (isSignedIn || nextStep?.signInStep === 'DONE') {
          // New password set, no MFA challenge => account has no TOTP: require setup.
          try { sessionStorage.setItem('mfaSetupRequired', '1'); } catch (e) { /* ignore */ }
          setAuth(true, email);
          setResetPasswordStep(null);
          setPasswordFlowType(null);
          setTempUser(null);
          doSuccess();
        } else if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
          // Existing MFA — ask for the code
          pushToNavigationStack('login');
          setMfaUser(true);
          setMfaStep('verify');
          setResetPasswordStep(null);
          setPasswordFlowType(null);
        }
      } else {
        // Use confirmResetPassword for email verification flow
        await confirmResetPassword({ username: resetEmail.trim(), confirmationCode: resetCode, newPassword });
        console.log('Password reset successful via email verification');
        
        // Clear reset password states and go back to login page
        setResetPasswordStep(null);
        setPasswordFlowType(null);
        setResetEmail('');
        setResetCode('');
        setNewPassword('');
        setConfirmPassword('');
        setResetError('');
        setResetSuccess(true);
        clearNavigationStack(); // Clear navigation stack after successful reset
        
        // Show success message - user can now login with their new password
      }
    } catch (error) {
      console.error('Password reset error:', error);
      if (error.name === 'CodeMismatchException') {
        setResetError('Invalid verification code. Please check your email and try again.');
        setResetPasswordStep('code');
        setResetCode('');
      } else if (error.name === 'ExpiredCodeException') {
        setResetError('Verification code has expired. Please request a new one.');
        setResetPasswordStep('email');
        setResetCode('');
      } else {
        setResetError('Failed to reset password. Please try again.');
      }
    } finally {
      setResetLoading(false);
    }
  };

  const isPasswordValid = () => {
    const password = newPassword;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const hasMinLength = password.length >= 8;
    
    return hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar && hasMinLength;
  };


  const handleResetPasswordBack = () => {
    if (resetPasswordStep === 'code') {
      // Go back to email step
      setResetPasswordStep('email');
      setResetCode('');
      setResetError('');
      popFromNavigationStack();
    } else if (resetPasswordStep === 'password') {
      // Check which flow we're in to navigate back correctly
      if (passwordFlowType === 'new-user') {
        // New user flow: go back to login form
        const previousPage = popFromNavigationStack();
        setResetPasswordStep(null);
        setPasswordFlowType(null);
        setNewPassword('');
        setConfirmPassword('');
        setResetError('');
        setTempUser(null); // Clear temp user when going back
        // Clear email/password fields to show fresh login form
        setEmail('');
        setPassword('');
      } else if (passwordFlowType === 'reset-password') {
        // Reset password flow: go back to code step
        setResetPasswordStep('code');
        setNewPassword('');
        setConfirmPassword('');
        setResetError('');
        popFromNavigationStack();
      } else {
        // Fallback: go back to code step (for backwards compatibility)
        setResetPasswordStep('code');
        setNewPassword('');
        setConfirmPassword('');
        setResetError('');
        popFromNavigationStack();
      }
    } else {
      // Go back to previous page (usually login)
      const previousPage = popFromNavigationStack();
      setResetPasswordStep(null);
      setPasswordFlowType(null);
      setResetEmail('');
      setResetCode('');
      setNewPassword('');
      setConfirmPassword('');
      setResetError('');
      setResetLoading(false);
      
      // If we came from login, we're already there, so no need to change state
      // The resetPasswordStep being null will show login form
    }
  };

  const handleMfaBack = () => {
    // Go back to previous page based on navigation stack
    const previousPage = popFromNavigationStack();
    
    // Clear MFA state
    setMfaStep(null);
    setMfaCode('');
    setMfaQrCode('');
    setMfaSecretKey('');
    setMfaError('');
    setMfaUser(null);
    
    // Check which flow we came from
    if (previousPage === 'new-user-password' || previousPage === 'reset-password') {
      // We came from password step - restore it with the correct flow type
      if (previousPage === 'new-user-password') {
        setPasswordFlowType('new-user');
      } else {
        setPasswordFlowType('reset-password');
      }
      setResetPasswordStep('password');
      // Keep tempUser so user can continue
    } else if (previousPage === 'login') {
      // Already showing login form (mfaStep is null)
      // Clear tempUser and flow type if we're going back to login
      setTempUser(null);
      setPasswordFlowType(null);
    }
    // Otherwise, we're going back to login by default
  };

  // Handle ESC key to close modal and Enter key for MFA steps
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (resetPasswordStep) {
          handleResetPasswordBack();
        } else if (mfaStep) {
          handleMfaBack();
        } else {
        doClose();
        }
      } else if (event.key === 'Enter') {
        // Handle Enter key for MFA steps
        if (mfaStep === 'setup' && mfaCode && mfaCode.length === 6) {
          handleMfaVerification(mfaUser, mfaCode);
        } else if (mfaStep === 'verify' && mfaCode && mfaCode.length === 6) {
          handleMfaCodeConfirm();
        }
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isVisible, doClose, mfaStep, mfaCode, mfaUser, resetPasswordStep]);

  // Reset MFA state when modal closes
  useEffect(() => {
    if (!isVisible) {
      resetMfaState();
      setResetSuccess(false); // Clear success message when modal closes
      setShowSuccessMessage(false);
      clearNavigationStack(); // Clear navigation history when modal closes
      setPasswordFlowType(null); // Clear flow type when modal closes
    }
  }, [isVisible]);

  useEffect(() => {
    if (resetPasswordStep !== 'password') {
      setShowNewPassword(false);
      setShowConfirmPassword(false);
    }
  }, [resetPasswordStep]);

  // Debug MFA step changes
  useEffect(() => {
    console.log('MFA step changed to:', mfaStep);
  }, [mfaStep]);

  // Auto-hide success message after 4 seconds
  useEffect(() => {
    if (resetSuccess) {
      setShowSuccessMessage(true);
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
        // Also clear resetSuccess after fade animation completes
        setTimeout(() => setResetSuccess(false), 300); // 300ms for fade animation
      }, 4000);
      
      return () => clearTimeout(timer);
    }
  }, [resetSuccess]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Set loading state
    setIsLoading(true);
    
    // Trim whitespace from email and password
    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();
    
    console.log('Login attempt with:', { email: trimmedEmail, password: '***' });
    console.log('API endpoint:', API);
    console.log('Form validation - Email valid:', trimmedEmail.includes('@'));
    console.log('Form validation - Password length:', trimmedPassword.length);
  
    if (!trimmedEmail.includes('@')) {
      setEmailError(true);
      setPasswordError(false);
      setIsLoading(false);
      return;
    }

    try {
      console.log('Attempting sign in...');
      
      // First, try to get the username (UUID) for this email
      const username = await getUsernameByEmail(trimmedEmail);
      
      const loginId = username || trimmedEmail;
      console.log(username
        ? `Found username ${username} for email ${trimmedEmail}`
        : 'Username not found, using email as username');

      const { isSignedIn, nextStep } = await signIn({ username: loginId, password: trimmedPassword });
      console.log('signIn result:', { isSignedIn, signInStep: nextStep?.signInStep });

      // Fully authenticated — no further challenge
      if (isSignedIn || nextStep.signInStep === 'DONE') {
        console.log('User is fully authenticated, no MFA required');
        // Password sign-in with NO MFA challenge => account has no TOTP: require setup.
        try { sessionStorage.setItem('mfaSetupRequired', '1'); } catch (e) { /* ignore */ }
        setAuth(true, trimmedEmail);
        doSuccess();
        setIsLoading(false);
        return;
      }

      // Existing MFA user — enter a TOTP code
      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        console.log('MFA required for existing user');
        pushToNavigationStack('login'); // Track that we came from login
        setMfaUser(true);
        setMfaStep('verify');
        setMfaError('');
        setIsLoading(false);
        return;
      }

      // New user — must set up TOTP MFA
      if (nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
        console.log('MFA setup required for new user');
        pushToNavigationStack('login'); // Track that we came from login
        await handleMfaSetup(nextStep.totpSetupDetails);
        setIsLoading(false);
        return;
      }

      // Temporary password — must choose a new password
      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        console.log('User must set a new password (temp password used)');

        // Verify email immediately since they proved access to their email
        try {
          const verifyResult = await verifyUserEmailInCognito(loginId, trimmedEmail);
          console.log('Email verified successfully after temp password usage:', verifyResult);
        } catch (verifyError) {
          console.warn('Could not verify email immediately (will try again later):', verifyError);
        }

        // NEW USER flow (temp password from login)
        pushToNavigationStack('login'); // Track that we came from login
        setTempUser({ username: loginId });
        setPasswordFlowType('new-user'); // Mark this as new user flow
        setResetPasswordStep('password');
        setResetEmail(trimmedEmail);
        setIsLoading(false);
        return;
      }

      // Fallback: unexpected sign-in step
      console.error('Unexpected sign-in step:', nextStep.signInStep);
      setEmailError(false);
      setPasswordError(true);
      setIsLoading(false);
      
    } catch (error) {
      console.error('Login error:', error);
      setIsLoading(false);
      
      // Check if it's a temporary password error
      if ((error.name || error.code) === 'NotAuthorizedException' && error.message?.includes('temporary')) {
        // Trigger forgot password flow for temporary password users
        // This is still part of the reset password flow, not new user flow
        try {
          await resetPassword({ username: trimmedEmail });
          pushToNavigationStack('login'); // Track that we came from login
          pushToNavigationStack('reset-password-email'); // Track email step
          setPasswordFlowType('reset-password'); // This is reset password flow
          setTempUser({ username: trimmedEmail });
          setResetPasswordStep('code');
          setResetEmail(trimmedEmail);
          setIsLoading(false);
          return;
        } catch (forgotError) {
          console.error('Forgot password error:', forgotError);
        }
      }
      
      setEmailError(false);
      setPasswordError(true);
    }
  };

  // Passkey (WebAuthn) sign-in. Tries the biometric factor first; on any
  // outcome that isn't a completed sign-in, resets Amplify's in-progress flow
  // (so the password form can start fresh) and falls back to password + TOTP.
  const handlePasskeyLogin = async (opts = {}) => {
    // `auto` = fired automatically on the login screen for a returning passkey
    // user (no tap). If the browser blocks an ungestured WebAuthn call, fall back
    // silently to the form (no error, no keyboard) instead of looking broken.
    const auto = opts && opts.auto === true;
    const trimmedEmail = email.trim();
    if (!trimmedEmail.includes('@')) {
      if (!auto) setEmailError(true);
      return;
    }
    setEmailError(false);
    setPasswordError(false);
    setPasskeyError('');
    setPasskeyBusy(true);

    const fallbackToPassword = async (message) => {
      // Clear any half-started USER_AUTH flow so a later password signIn is clean.
      try { await signOut(); } catch (e) { /* not signed in — ignore */ }
      // Remember that the user tried biometric this session → after they sign in
      // with a password, offer to enroll a passkey for next time (consumed once
      // by PasskeyEnrollPrompt). Nothing nags users who never click this button.
      // Actively tapping the passkey button overrides a past "Not now" dismissal.
      try {
        sessionStorage.setItem('passkeyIntent', '1');
        localStorage.removeItem(`passkeyEnrollDismissed:${trimmedEmail}`);
      } catch (e) { /* ignore */ }
      if (!auto) {
        setPasskeyError(message);
        setTimeout(() => document.getElementById('password')?.focus(), 0);
      }
    };

    try {
      const loginId = (await getUsernameByEmail(trimmedEmail)) || trimmedEmail;
      let { isSignedIn, nextStep } = await signIn({
        username: loginId,
        options: { authFlowType: 'USER_AUTH', preferredChallenge: 'WEB_AUTHN' },
      });
      console.log('Passkey signIn step:', nextStep?.signInStep, 'available:', nextStep?.availableChallenges);

      // Cognito may respond with a factor-selection step instead of honouring the
      // preferred challenge. If WEB_AUTHN is on offer, select it explicitly —
      // without this the flow looked like "no passkey" even when one existed.
      if (!isSignedIn && nextStep?.signInStep === 'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION') {
        const available = nextStep.availableChallenges || [];
        if (available.includes('WEB_AUTHN')) {
          ({ isSignedIn, nextStep } = await confirmSignIn({ challengeResponse: 'WEB_AUTHN' }));
          console.log('After WEB_AUTHN selection:', { isSignedIn, step: nextStep?.signInStep });
        }
      }

      if (isSignedIn) {
        // Remember this device used a passkey → lead with it next time.
        try { localStorage.setItem('rdvPasskeyDevice', '1'); } catch (e) { /* ignore */ }
        setAuth(true, trimmedEmail);
        doSuccess();
        return;
      }

      // Cognito offered other factors instead of WebAuthn — no usable passkey here.
      const stepInfo = `${nextStep?.signInStep || 'unknown'}${nextStep?.availableChallenges ? ` / ${nextStep.availableChallenges.join(',')}` : ''}`;
      console.log('Passkey not available; sign-in nextStep:', stepInfo);
      await fallbackToPassword(`No passkey usable for this account on this device. Enter your password to sign in. [${stepInfo}]`);
    } catch (error) {
      console.warn('Passkey sign-in failed:', error);
      const errName = error?.name || error?.code;
      if (errName === 'UserAlreadyAuthenticatedException') {
        setAuth(true, trimmedEmail);
        doSuccess();
        return;
      }
      if (errName === 'NotAllowedError' || /cancel|timed out|not allowed/i.test(error?.message || '')) {
        await fallbackToPassword('Biometric prompt was cancelled. Try again or sign in with your password.');
      } else {
        await fallbackToPassword(`Could not sign in with a passkey. Please use your password. [${errName || 'Error'}: ${error?.message || 'unknown'}]`);
      }
    } finally {
      setPasskeyBusy(false);
    }
  };

  // Returning passkey user on this device: auto-fire the passkey prompt when the
  // login form is shown (email already prefilled). Runs at most once; if the
  // browser blocks the ungestured call it silently falls back to the form, where
  // the primary passkey button (a real tap) still works.
  useEffect(() => {
    if (autoPasskeyTriedRef.current) return;
    if (!isVisible || mfaStep || resetPasswordStep) return;
    if (!PASSKEY_SUPPORTED) return;
    let deviceHasPasskey = false;
    try { deviceHasPasskey = localStorage.getItem('rdvPasskeyDevice') === '1'; } catch (e) { /* ignore */ }
    if (!deviceHasPasskey) return;
    if (!email || !email.includes('@')) return;
    autoPasskeyTriedRef.current = true;
    handlePasskeyLogin({ auto: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, mfaStep, resetPasswordStep, email]);

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    if (emailError) setEmailError(false);
    if (passkeyError) setPasskeyError('');
    setResetSuccess(false); // Clear success message when user starts typing
    setShowSuccessMessage(false);
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
    if (passwordError) setPasswordError(false);
    setResetSuccess(false); // Clear success message when user starts typing
    setShowSuccessMessage(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    // Start the reset password flow
    console.log('Starting reset password flow');
    pushToNavigationStack('login'); // Track that we came from login
    setPasswordFlowType(null); // Will be set to 'reset-password' when we reach password step
    setResetPasswordStep('email');
    setResetEmail('');
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setResetError('');
    setResetLoading(false);
  };

  const handleGoogleLogin = () => console.log('Google login');
  const handleGitHubLogin = () => console.log('GitHub login');
  const handleFacebookLogin = () => console.log('Facebook login');

  // Returning-user nicety: if this device has signed in with a passkey before,
  // lead with the passkey as the primary one-tap action (email already prefilled).
  const passkeyDevice = (() => {
    try { return localStorage.getItem('rdvPasskeyDevice') === '1'; } catch (e) { return false; }
  })();

  const renderPasskeyButton = ({ primary }) => (
    <>
      {!primary && <div className="login-divider"><span>or</span></div>}
      <button
        type="button"
        className={`passkey-signin-button ${primary ? 'passkey-primary' : ''}`}
        onClick={() => handlePasskeyLogin()}
        disabled={passkeyBusy || isLoading}
      >
        <FingerprintIcon />
        {passkeyBusy ? 'Waiting for biometric…' : 'Sign in with a passkey'}
      </button>
      {passkeyError && <span className="error-text passkey-error-text">{passkeyError}</span>}
      {primary && <div className="login-divider"><span>or use your password</span></div>}
    </>
  );

  if (!isVisible) return null;

  const content = (
    <>
    <div className="modal-container" onClick={(e) => !asPage && e.stopPropagation()}>
        <div className={`modern-modal ${!mfaStep ? 'auth-modal' : ''} ${mfaStep ? 'mfa-modal' : ''} ${mfaStep === 'setup' ? 'mfa-setup-modal' : mfaStep === 'verify' ? 'mfa-verify-modal' : resetPasswordStep ? 'reset-password-modal' : 'login-modal'}`}>

          {/* Left Side - Image with Logo */}
          <div className="image-section">
            <div className="logo-container">
              <img src={rdv} alt="RDV Systems" className="rdv-logo" />
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className={`login-section ${!mfaStep ? 'auth-panel' : ''} ${mfaStep ? 'mfa-panel' : ''} ${mfaStep === 'setup' ? 'mfa-setup-section' : mfaStep === 'verify' ? 'mfa-verify-section' : resetPasswordStep ? 'reset-password-section' : 'login-form-section'}`}>
            {/* Close Button */}
            <button 
              className="login-close-button" 
              onClick={() => {
                // Smart navigation based on current state and navigation stack
                if (resetPasswordStep) {
                  handleResetPasswordBack();
                } else if (mfaStep) {
                  handleMfaBack();
                } else {
                  // On login page, close or go back
                  doClose();
                }
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            
            <div className={`login-content ${!mfaStep ? 'auth-panel-content' : ''} ${mfaStep ? 'mfa-panel-content' : ''} ${mfaStep === 'setup' ? 'mfa-setup-content' : mfaStep === 'verify' ? 'mfa-verify-content' : resetPasswordStep ? 'reset-password-content' : 'login-form-content'}`}>
              {/* Header */}
              <div className={`login-header ${!mfaStep && !resetPasswordStep ? 'login-form-header' : mfaStep === 'setup' ? 'mfa-setup-header' : mfaStep === 'verify' ? 'mfa-verify-header' : 'reset-password-header'}`}>
                <div className="login-avatar">
                  {/* Login Step Avatar - User Icon */}
                  {!mfaStep && !resetPasswordStep && (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  )}
                  
                  {/* MFA Setup Step Avatar - Shield with Checkmark */}
                  {mfaStep === 'setup' && (
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                  
                  {/* MFA Verification Step Avatar - Lock Icon */}
                  {mfaStep === 'verify' && (
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="16" r="1" fill="currentColor"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}


                  {/* Reset Password Step Avatar - Key Icon */}
                  {resetPasswordStep && (
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
                
                {/* Dynamic Title based on step */}
                <h1 className="title">
                  {!mfaStep && !resetPasswordStep}
                  {mfaStep === 'setup' && 'Setup Two-Factor Authentication'}
                  {mfaStep === 'verify' && 'Additional verification required'}
                  {resetPasswordStep === 'email' && 'Reset Password'}
                  {resetPasswordStep === 'code' && 'Enter Verification Code'}
                  {resetPasswordStep === 'password' && 'Set New Password'}
                </h1>
                
                <p className={`subtitle ${mfaStep === 'setup' ? 'mfa-setup-subtitle' : mfaStep === 'verify' ? 'mfa-verify-subtitle' : resetPasswordStep ? 'reset-password-subtitle' : 'login-subtitle'}`}>
                  {mfaStep === 'setup' 
                    ? 'Scan the QR code with your Authenticator app' 
                    : mfaStep === 'verify' 
                    ? '' 
                    : resetPasswordStep === 'email'
                    ? 'Enter your email address to receive a verification code'
                    : resetPasswordStep === 'code'
                    ? 'Enter the verification code sent to your email'
                    : resetPasswordStep === 'password'
                    ? 'Create a new password for your account'
                    : 'Sign into your account'
                  }
                </p>
              </div>

              {/* MFA Setup Step */}
              {mfaStep === 'setup' && (
                <div className="mfa-setup-container mfa-with-qr">
                  <div className="mfa-header">
                    <button 
                      type="button" 
                      className="mfa-help-link"
                      onClick={() => setShowMfaGuide(true)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                        <path d="M9.09 9C9.3251 8.33167 9.78915 7.76811 10.4 7.40913C11.0108 7.05016 11.7289 6.91894 12.4272 7.03871C13.1255 7.15849 13.7588 7.52152 14.2151 8.06353C14.6713 8.60553 14.9211 9.29152 14.92 10C14.92 12 11.92 13 11.92 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12 17H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Need help with Authenticator apps?
                    </button>
                  </div>
                  
                  <div className="qr-code-container">
                    <div className="qr-code">
                      {mfaQrCode ? (
                        <img 
                          src={mfaQrCode} 
                          alt="QR Code for Authenticator App" 
                          className="qr-code-image"
                        />
                      ) : (
                        <div className="qr-placeholder">
                          <p>Generating QR Code...</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="input-group">
                    <label htmlFor="mfaCode" className="input-label">Enter 6-digit MFA code:</label>
                      <div className="mfa-code-input-container">
                        {[0, 1, 2, 3, 4, 5].map((index) => (
                      <input
                            key={index}
                        type="verification"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            autoComplete="one-time-code"
                            className={`mfa-code-input ${mfaCode[index] ? 'filled' : ''} ${mfaError && mfaError.includes('verification code') ? 'error' : ''}`}
                            value={mfaCode[index] || ''}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '');
                              if (value.length <= 1) {
                                const newCode = mfaCode.split('');
                                newCode[index] = value;
                                const updatedCode = newCode.join('').slice(0, 6);
                                setMfaCode(updatedCode);
                                
                                // Clear error when user starts typing
                                if (mfaError) {
                                  setMfaError('');
                                }
                                
                                // Auto-focus next input or submit if last digit
                                if (value) {
                                  if (index < 5) {
                                    const nextInput = document.querySelector(`.mfa-code-input:nth-child(${index + 2})`);
                                    if (nextInput) nextInput.focus();
                                  } else {
                                    // Last digit entered, auto-submit
                                    handleMfaVerification(mfaUser, updatedCode);
                                  }
                                }
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && index === 5) {
                                e.preventDefault();
                                const container = e.target.closest('.mfa-code-input-container');
                                const inputs = container?.querySelectorAll('.mfa-code-input');
                                const fullCode = inputs ? Array.from(inputs).map(i => i.value).join('') : '';
                                if (fullCode.length === 6) handleMfaVerification(mfaUser, fullCode);
                              } else if (e.key === 'Backspace' && !mfaCode[index] && index > 0) {
                                const prevInput = document.querySelector(`.mfa-code-input:nth-child(${index})`);
                                if (prevInput) prevInput.focus();
                              }
                            }}
                            onPaste={(e) => {
                              e.preventDefault();
                              const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                              if (pastedData.length > 0) {
                                setMfaCode(pastedData);
                                setMfaError('');
                                
                                const targetIndex = Math.min(pastedData.length - 1, 5);
                                const targetInput = document.querySelector(`.mfa-code-input:nth-child(${targetIndex + 1})`);
                                if (targetInput) {
                                  if (pastedData.length === 6) {
                                    setTimeout(() => handleMfaVerification(mfaUser, pastedData), 100);
                                  } else {
                                    targetInput.focus();
                                  }
                                }
                              }
                            }}
                            maxLength="1"
                        required
                            autoFocus={index === 0}
                      />
                        ))}
                    </div>
                    {mfaError && !mfaVerifying && (
                      <div className="mfa-error-container">
                        <span className="error-text">{mfaError}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="mfa-buttons">
                    <button 
                      type="button" 
                      className="signin-button"
                      onClick={() => handleMfaVerification(mfaUser, mfaCode)}
                      disabled={mfaVerifying}
                    >
                      {mfaVerifying ? 'Verifying...' : 'Sign in'}
                    </button>
                  </div>
                </div>
              )}

              {/* MFA Verification Step */}
              {mfaStep === 'verify' && (
                <div className="mfa-verify-container mfa-without-qr">
                  <div className="mfa-header">
                    <h2>Two-Factor Authentication</h2>
                    <p>To finish signing in, enter the 6-digit code from your <br></br><a href="#" onClick={() => setShowMfaGuide(true)} className="mfa-inline-link">MFA Authenticator app</a></p>
                  </div>
                  
                  <div className="input-group">
                    <label htmlFor="mfaCode" className="input-label">Verification Code:</label>
                    <div className="mfa-code-input-container">
                      {[0, 1, 2, 3, 4, 5].map((index) => (
                      <input
                          key={index}
                        type="verification"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          autoComplete="one-time-code"
                          className={`mfa-code-input ${mfaCode[index] ? 'filled' : ''} ${mfaError && mfaError.includes('verification code') ? 'error' : ''}`}
                          value={mfaCode[index] || ''}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            if (value.length <= 1) {
                              const newCode = mfaCode.split('');
                              newCode[index] = value;
                              const updatedCode = newCode.join('').slice(0, 6);
                              setMfaCode(updatedCode);
                              
                              // Clear error when user starts typing
                              if (mfaError) {
                                setMfaError('');
                              }
                              
                              // Auto-focus next input or submit if last digit
                              if (value) {
                                if (index < 5) {
                                  const nextInput = document.querySelector(`.mfa-code-input:nth-child(${index + 2})`);
                                  if (nextInput) nextInput.focus();
                                } else {
                                  // Last digit entered, auto-submit
                                  handleMfaCodeConfirm(updatedCode);
                                }
                              }
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && index === 5) {
                              e.preventDefault();
                              const container = e.target.closest('.mfa-code-input-container');
                              const inputs = container?.querySelectorAll('.mfa-code-input');
                              const fullCode = inputs ? Array.from(inputs).map(i => i.value).join('') : '';
                              if (fullCode.length === 6) handleMfaCodeConfirm(fullCode);
                            } else if (e.key === 'Backspace' && !mfaCode[index] && index > 0) {
                              const prevInput = document.querySelector(`.mfa-code-input:nth-child(${index})`);
                              if (prevInput) prevInput.focus();
                            }
                          }}
                          onPaste={(e) => {
                            e.preventDefault();
                            const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                            if (pastedData.length > 0) {
                              setMfaCode(pastedData);
                              setMfaError('');
                              
                              const targetIndex = Math.min(pastedData.length - 1, 5);
                              const targetInput = document.querySelector(`.mfa-code-input:nth-child(${targetIndex + 1})`);
                              if (targetInput) {
                                if (pastedData.length === 6) {
                                  setTimeout(() => handleMfaCodeConfirm(pastedData), 100);
                                } else {
                                  targetInput.focus();
                                }
                              }
                            }
                          }}
                          maxLength="1"
                        required
                          autoFocus={index === 0}
                      />
                      ))}
                    </div>
                    {mfaError && !mfaVerifying && (
                      <div className="mfa-error-container">
                        <span className="error-text">{mfaError}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="mfa-buttons">
                    <button 
                      type="button"
                      className="signin-button"
                      onClick={handleMfaCodeConfirm}
                      disabled={mfaVerifying}
                    >
                      {mfaVerifying ? 'Verifying...' : 'Sign In'}
                    </button>
                  </div>
                      </div>
                    )}


              {/* Reset Password Email Step */}
              {resetPasswordStep === 'email' && (
                <div className="reset-password-email-container">
                  <div className="input-group">
                    {/* <label htmlFor="resetEmail" className="input-label">Email Address:</label> */}
                    <div className="input-wrapper">
                      <input
                        type="email"
                        id="resetEmail"
                        className="input-field"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        placeholder="Enter your email address"
                        required
                        autoFocus
                      />
                    </div>
                  </div>
                  
                  {resetError && (
                    <div className="error-container">
                      <span className="error-text">{resetError}</span>
                    </div>
                  )}
                  
                  <div className="reset-password-buttons">
                    <button 
                      type="button" 
                      className="signin-button"
                      onClick={handleSendResetCode}
                      disabled={resetLoading}
                    >
                      {resetLoading ? 'Sending...' : 'Send Verification Code'}
                    </button>
                  </div>
                </div>
              )}

              {/* Reset Password Code Step */}
              {resetPasswordStep === 'code' && (
                <div className="reset-password-code-container">
                  <div className="input-group">
                    <label htmlFor="resetCode" className="input-label">Enter 6-digit verification code:</label>
                    <div className="reset-password-code-input-container">
                      {[0, 1, 2, 3, 4, 5].map((index) => (
                        <input
                          key={index}
                          type="verification"
                          className={`reset-password-code-input ${resetCode[index] ? 'filled' : ''} ${resetError && resetError.includes('verification code') ? 'error' : ''}`}
                          value={resetCode[index] || ''}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            if (value.length <= 1) {
                              const newCode = resetCode.split('');
                              newCode[index] = value;
                              const updatedCode = newCode.join('').slice(0, 6);
                              setResetCode(updatedCode);

                              // Clear error when user starts typing
                              if (resetError) {
                                setResetError('');
                              }

                              // Auto-focus next input or proceed to password step
                              if (value) {
                                if (index < 5) {
                                  const nextInput = document.querySelector(`.reset-password-code-input:nth-child(${index + 2})`);
                                  if (nextInput) nextInput.focus();
                                } else {
                                  // Last digit entered, proceed to password step
                                  // This is the RESET PASSWORD flow
                                  e.target.blur();
                                  pushToNavigationStack('reset-password-code'); // Track that we came from code step
                                  setPasswordFlowType('reset-password'); // Mark this as reset password flow
                                  setTimeout(() => {
                                    setResetPasswordStep('password');
                                  }, 500);
                                }
                              }
                            }
                          }}
                          onKeyDown={(e) => {
                            // Handle backspace to move to previous input
                            if (e.key === 'Backspace' && !resetCode[index] && index > 0) {
                              const prevInput = document.querySelector(`.reset-password-code-input:nth-child(${index})`);
                              if (prevInput) prevInput.focus();
                            }
                          }}
                          onPaste={(e) => {
                            e.preventDefault();
                            const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                            if (pastedData.length > 0) {
                              setResetCode(pastedData);
                              setResetError('');

                              // Focus the appropriate input based on pasted data length
                              const targetIndex = Math.min(pastedData.length - 1, 5);
                              const targetInput = document.querySelector(`.reset-password-code-input:nth-child(${targetIndex + 1})`);
                              if (targetInput) {
                                if (pastedData.length === 6 && targetIndex === 5) {
                                  // If all 6 digits are pasted, proceed to password step
                                  // This is the RESET PASSWORD flow
                                  pushToNavigationStack('reset-password-code'); // Track that we came from code step
                                  setPasswordFlowType('reset-password'); // Mark this as reset password flow
                                  setTimeout(() => {
                                    setResetPasswordStep('password');
                                  }, 500);
                                } else {
                                  targetInput.focus();
                                }
                              }
                            }
                          }}
                          maxLength="1"
                          required
                          autoFocus={index === 0}
                        />
                      ))}
                    </div>
                    {resetError && (
                      <div className="reset-password-error-container">
                        <span className="error-text">{resetError}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="reset-password-buttons">
                    <button 
                      type="button" 
                      className="signin-button"
                      onClick={() => {
                        // This is the RESET PASSWORD flow
                        pushToNavigationStack('reset-password-code'); // Track that we came from code step
                        setPasswordFlowType('reset-password'); // Mark this as reset password flow
                        setResetPasswordStep('password');
                      }}
                      disabled={resetCode.length !== 6}
                    >
                      Continue
                    </button>
                  </div>
                </div>
              )}

              {/* Reset Password Password Step */}
              {resetPasswordStep === 'password' && (
                <div className="reset-password-password-container">
                  <div className="input-group">
                    {/* <label htmlFor="newPassword" className="input-label">New Password:</label> */}
                    <div className="input-wrapper">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        id="newPassword"
                        className="input-field"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter your new password"
                        required
                        autoFocus
                      />
                      <button
                        type="button"
                        className="reset-password-toggle"
                        onClick={() => setShowNewPassword((prev) => !prev)}
                        aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                      >
                        {showNewPassword ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.94 17.94C16.2306 19.243 14.1491 19.9649 12 20C5 20 1 12 1 12C2.24389 9.68192 3.96914 7.65661 6.06 6.06M9.9 4.24C10.5883 4.0789 11.2931 3.99836 12 4C19 4 23 12 23 12C22.393 13.1356 21.6691 14.2048 20.84 15.19M14.12 14.12C13.8454 14.4148 13.5141 14.6512 13.1462 14.8151C12.7782 14.9791 12.3809 15.0673 11.9781 15.0744C11.5753 15.0815 11.1747 15.0074 10.8019 14.8565C10.4291 14.7056 10.0897 14.4811 9.80385 14.1952C9.51797 13.9093 9.29339 13.5699 9.14251 13.1971C8.99162 12.8243 8.91753 12.4237 8.92463 12.0209C8.93174 11.6181 9.01991 11.2208 9.18388 10.8528C9.34784 10.4848 9.58426 10.1535 9.88 9.88M1 1L23 23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="input-group">
                    {/* <label htmlFor="confirmPassword" className="input-label">Confirm Password:</label> */}
                    <div className="input-wrapper">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        className="input-field"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm your new password"
                        required
                      />
                      <button
                        type="button"
                        className="reset-password-toggle"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                      >
                        {showConfirmPassword ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.94 17.94C16.2306 19.243 14.1491 19.9649 12 20C5 20 1 12 1 12C2.24389 9.68192 3.96914 7.65661 6.06 6.06M9.9 4.24C10.5883 4.0789 11.2931 3.99836 12 4C19 4 23 12 23 12C22.393 13.1356 21.6691 14.2048 20.84 15.19M14.12 14.12C13.8454 14.4148 13.5141 14.6512 13.1462 14.8151C12.7782 14.9791 12.3809 15.0673 11.9781 15.0744C11.5753 15.0815 11.1747 15.0074 10.8019 14.8565C10.4291 14.7056 10.0897 14.4811 9.80385 14.1952C9.51797 13.9093 9.29339 13.5699 9.14251 13.1971C8.99162 12.8243 8.91753 12.4237 8.92463 12.0209C8.93174 11.6181 9.01991 11.2208 9.18388 10.8528C9.34784 10.4848 9.58426 10.1535 9.88 9.88M1 1L23 23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Password Requirements */}
                  <div className="reset-password-requirements">
                    <div className="reset-password-requirements-title">Password Requirements:</div>
                    <ul className="reset-password-requirements-list">
                      <li className={newPassword.length >= 8 ? 'valid' : ''}>At least 8 characters long</li>
                      <li className={/[A-Z]/.test(newPassword) ? 'valid' : ''}>Contains uppercase letter</li>
                      <li className={/[a-z]/.test(newPassword) ? 'valid' : ''}>Contains lowercase letter</li>
                      <li className={/\d/.test(newPassword) ? 'valid' : ''}>Contains number</li>
                      <li className={/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? 'valid' : ''}>Contains special character</li>
                      <li className={newPassword === confirmPassword && newPassword.length > 0 ? 'valid' : ''}>Passwords match</li>
                    </ul>
                  </div>
                  
                  {resetError && (
                    <div className="error-container">
                      <span className="error-text">{resetError}</span>
                    </div>
                  )}
                  
                  <div className="reset-password-buttons">
                    <button 
                      type="button" 
                      className="signin-button"
                      onClick={handlePasswordSubmit}
                      disabled={resetLoading || !isPasswordValid() || newPassword !== confirmPassword}
                    >
                      {resetLoading ? 'Setting Password...' : 'Set New Password'}
                    </button>
                  </div>
                </div>
              )}

              {/* Regular Login Form */}
              {!mfaStep && !resetPasswordStep && (
                <>
                <form className="login-form login-main-form" onSubmit={handleSubmit}>

                {PASSKEY_SUPPORTED && passkeyDevice && renderPasskeyButton({ primary: true })}

                <div className="input-group">
                  {/* <label htmlFor="email" className="input-label">Email Address:</label> */}
                  <div className="input-wrapper">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className={`glass-input ${emailError ? 'error' : ''}`}
                      placeholder="Enter your email"
                      value={email}
                      onChange={handleEmailChange}
                      required
                    />
                  </div>
                  {emailError && <span className="error-text">Please enter a valid email address</span>}
                </div>

                <div className="input-group">
                  {/* <label htmlFor="password" className="input-label">Password:</label> */}
                  <div className="input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      className={`glass-input ${passwordError ? 'error' : ''}`}
                      placeholder="Enter your password"
                      value={password}
                      onChange={handlePasswordChange}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.94 17.94C16.2306 19.243 14.1491 19.9649 12 20C5 20 1 12 1 12C2.24389 9.68192 3.96914 7.65661 6.06 6.06M9.9 4.24C10.5883 4.0789 11.2931 3.99836 12 4C19 4 23 12 23 12C22.393 13.1356 21.6691 14.2048 20.84 15.19M14.12 14.12C13.8454 14.4148 13.5141 14.6512 13.1462 14.8151C12.7782 14.9791 12.3809 15.0673 11.9781 15.0744C11.5753 15.0815 11.1747 15.0074 10.8019 14.8565C10.4291 14.7056 10.0897 14.4811 9.80385 14.1952C9.51797 13.9093 9.29339 13.5699 9.14251 13.1971C8.99162 12.8243 8.91753 12.4237 8.92463 12.0209C8.93174 11.6181 9.01991 11.2208 9.18388 10.8528C9.34784 10.4848 9.58426 10.1535 9.88 9.88M1 1L23 23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </button>
                  </div>
                  {passwordError && <span className="error-text">Invalid email or password</span>}
                </div>

                {/* Forgot Password */}
                <div className="forgot-password">
                  <a href="#" className="forgot-link" onClick={handleForgotPassword}>Forgot your password?</a>
                </div>

                {/* Sign In Button */}
                <button type="submit" className="signin-button" disabled={isLoading}>
                  {isLoading ? 'Signing In...' : 'Sign In'}
                </button>

                {/* Passkey sign-in — primary at top for returning passkey users; otherwise below */}
                {PASSKEY_SUPPORTED && !passkeyDevice && renderPasskeyButton({ primary: false })}

              </form>

              {/* Social Media Links */}
              <div className="social-media-links">
                <a 
                  href="https://www.facebook.com/RDVSystems" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-media-link"
                  aria-label="Facebook"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" fill="white"/>
                  </svg>
                </a>
                <a 
                  href="https://www.instagram.com/rdvsystems/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-media-link"
                  aria-label="Instagram"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" stroke="white" strokeWidth="2" fill="none"/>
                    <circle cx="12" cy="12" r="4" stroke="white" strokeWidth="2" fill="none"/>
                    <circle cx="17.5" cy="6.5" r="1.5" fill="white"/>
                  </svg>
                </a>
                <a 
                  href="https://www.youtube.com/@RDVIZ" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-media-link"
                  aria-label="YouTube"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z" fill="white"/>
                    <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" fill="rgba(0,0,0,0.7)"/>
                  </svg>
                </a>
                <a 
                  href="https://www.linkedin.com/company/rdv-systems/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-media-link"
                  aria-label="LinkedIn"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" fill="white"/>
                    <rect x="2" y="9" width="4" height="12" fill="white"/>
                    <circle cx="4" cy="4" r="2" fill="white"/>
                  </svg>
                </a>
              </div>
                </>
              )}
            </div>

            {/* Copyright Notice */}
            <div className="login-copyright">
              © 2009 - {currentYear} RDV Systems. ALL RIGHTS RESERVED.
            </div>
          </div>
        </div>

      <MfaGuideModal
        open={showMfaGuide}
        onClose={() => setShowMfaGuide(false)}
      />
    </div>
    </>
  );

  return asPage ? (
    <div className="login-page-inner">{content}</div>
  ) : (
    <div className="modal-overlay">{content}</div>
  );
};

export default LoginModal;
